import { FC, useMemo } from "react";
import { Divider, Empty } from "antd";
import { useTranslation } from "react-i18next";
import NoData from "@Components/NoData/NoData";
import { parse, type StyleOptions } from "src/htmlParser";
import { FARE_RULES_SKELETON, type FareRule } from "src/Modules/Flights/constants/JourneyDetails";
import { useBridge } from "src/context/BridgeContext";
import { JourneyStaticContextType } from "../../context/JourneyStaticDetails";
import ParsedHtml from "./components/ParsedHtml";

const FARE_RULE_STYLES: StyleOptions = {
  columnStyles: "gap-4",
  pieceStyles: "flex flex-col gap-2",
  tableStyles: "w-full border-collapse overflow-x-scroll",
  cellStyles: "border border-border-default p-2 text-center align-center items-center",
  thStyles: "bg-background-subtle font-semibold whitespace-nowrap!",
  listStyles: "flex flex-col gap-1 ps-5 items-start",
  ulStyles: "list-disc",
  tdStyles:"whitespace-nowrap!",
  olStyles: "list-decimal",
  listLabelStyles: "block font-semibold",
  fieldsetStyles: "rounded-md border border-border-default p-3",
  legendStyles: "px-1 font-semibold",
  h1Styles: "font-semibold",
  h2Styles: "font-semibold",
  h3Styles: "font-semibold",
  h4Styles: "font-semibold",
  aStyles: "text-font-active underline",
  pairStyles: "flex gap-3 items-center",
  pairLabelStyles: "text-font-subtle",
  pairValueStyles: "font-semibold whitespace-nowrap",
};
const FARE_RULE_LEVEL_SIZES = ["text-lg", "text-base", "text-sm", "text-xs"];

const FareRuleDetail: FC<{ html: string }> = ({ html }) => {
  const nodes = useMemo(() => (html ? parse(html.replaceAll("\\n", "<br>"), FARE_RULE_STYLES) : null), [html]);
  if (!nodes) return <NoData />;
  return <ParsedHtml nodes={nodes} className="text-font-default flex flex-col gap-2 text-sm" levelClassNames={FARE_RULE_LEVEL_SIZES} />;
};

export default function FareRules() {
  const { t } = useTranslation();
  const { journeyDetails } = useBridge<JourneyStaticContextType>();
  const { data, error, isFetching } = journeyDetails;
  const fareRules = data?.fareRules;
  const isEmpty = fareRules && fareRules.length == 0;
  if (isFetching) return FARE_RULES_SKELETON;
  if (error) return <div>{t("Flights.details.fareRules.notFound")}</div>;
  if (isEmpty) return <Empty />;
  return fareRules?.map((rule: FareRule, index: number) => {
    return (
      <div key={`rule-${index}`} className="custom-scrollbar">
        <FareRuleDetail html={rule.ruleDetail} />
      </div>
    );
  });
}
